import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Globe2, Loader2, MapPin, ShieldCheck, Trophy, Users, TrendingUp, TrendingDown } from "lucide-react";
import { AdminShell } from "@/components/admin/admin-shell";
import { requireRole } from "@/lib/session";
import { useSchoolContext } from "@/lib/school-context";
import { nationalBenchmarks } from "@/lib/benchmarking.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/benchmarking")({
  ssr: false,
  beforeLoad: () => requireRole("admin"),
  head: () => ({
    meta: [
      { title: "National Benchmarking · MAKAI" },
      { name: "description", content: "Compare your school nationally with fully anonymised percentile, district, regional, subject, topic and class rankings." },
      { property: "og:title", content: "National Benchmarking · MAKAI" },
      { property: "og:description", content: "Anonymised national performance benchmarking for Ugandan schools." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

const PCT = (n: number | null | undefined) => (n == null ? "—" : `${n}%`);
const ORD = (n: number | null | undefined, total?: number | null) =>
  n == null ? "—" : `#${n}${total ? ` of ${total}` : ""}`;

function Page() {
  const { active } = useSchoolContext();
  const schoolId = active?.school_id ?? "";
  const fetchBenchmarks = useServerFn(nationalBenchmarks);

  const { data, isLoading, error } = useQuery({
    queryKey: ["national-benchmarks", schoolId],
    enabled: !!schoolId,
    queryFn: () => fetchBenchmarks({ data: { schoolId } }),
  });

  return (
    <AdminShell
      title="National Benchmarking"
      subtitle="See how your school compares nationally — every figure is aggregated and anonymised."
    >
      <div className="mb-6 flex items-start gap-3 rounded-2xl border border-border bg-primary-soft/40 p-4">
        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <p className="text-sm text-muted-foreground">
          Privacy first: no learner names, schools or identities are ever shown. Other schools appear as
          anonymous codes, and any group with fewer than {data?.minCohort ?? 5} learners is withheld from
          the comparison.
        </p>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Building the national comparison…
        </div>
      )}
      {error && <p className="text-sm text-destructive">Could not load benchmarks: {(error as Error).message}</p>}

      {data && data.suppressed && (
        <p className="rounded-2xl border border-border bg-card p-5 text-sm text-muted-foreground shadow-soft">
          Your school does not yet have enough published assessment results ({data.minCohort}+ learners) to be
          benchmarked nationally. Publish more assessments and this page will populate automatically.
        </p>
      )}

      {data && !data.suppressed && (
        <div className="space-y-6">
          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={Globe2} label="National percentile" value={PCT(data.school.nationalPercentile)}
              note={`Ahead of ${data.school.nationalPercentile ?? 0}% of benchmarked schools`} />
            <Stat icon={Trophy} label="National ranking" value={ORD(data.school.nationalRank, data.school.nationalTotal)}
              note={`Average ${PCT(data.school.avgPct)} vs national ${PCT(data.national.avgPct)}`} />
            <Stat icon={MapPin} label="District ranking" value={ORD(data.school.districtRank, data.school.districtTotal)}
              note={data.school.district} />
            <Stat icon={Users} label="Regional ranking" value={ORD(data.school.regionRank, data.school.regionTotal)}
              note={`${data.school.region} region`} />
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="font-display text-lg font-bold">Against the national average</h2>
              <span className={cn("inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold",
                (data.school.vsNationalPct ?? 0) >= 0 ? "bg-primary-soft text-primary" : "bg-destructive/10 text-destructive")}>
                {(data.school.vsNationalPct ?? 0) >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                {(data.school.vsNationalPct ?? 0) >= 0 ? "+" : ""}{data.school.vsNationalPct ?? 0} pts
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {data.national.schoolsBenchmarked} schools across {data.national.districts} districts and{" "}
              {data.national.learnersBenchmarked} anonymised learners are in this comparison.
            </p>
            <Bar label="Your school" value={data.school.avgPct ?? 0} highlight />
            <Bar label="National average" value={data.national.avgPct} />
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <h2 className="font-display text-lg font-bold">Anonymous national leaderboard</h2>
            <div className="mt-3 space-y-1">
              {data.leaderboard.map((r) => (
                <div key={r.position}
                  className={cn("flex items-center justify-between rounded-xl px-3 py-2 text-sm",
                    r.isMine ? "bg-primary-soft font-semibold text-primary" : "odd:bg-muted/40")}>
                  <span className="truncate">{r.position}. {r.label}</span>
                  <span className="tabular-nums">{PCT(r.avgPct)} · {r.region}</span>
                </div>
              ))}
              {data.leaderboard.length === 0 && <p className="text-sm text-muted-foreground">Not enough data yet.</p>}
            </div>
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <h2 className="font-display text-lg font-bold">Learner percentiles (anonymised)</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-4">
              {data.learners.bands.map((b) => (
                <div key={b.band} className="rounded-xl border border-border p-3">
                  <div className="font-display text-2xl font-bold">{b.count}</div>
                  <div className="text-xs text-muted-foreground">{b.band}</div>
                </div>
              ))}
            </div>
            <Table
              className="mt-4"
              head={["Learner code", "Average", "National percentile"]}
              rows={data.learners.rows.slice(0, 25).map((l) => [l.label, PCT(l.avgPct), PCT(l.nationalPercentile)])}
              empty="No learner has enough evidence to be ranked yet."
            />
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <h2 className="font-display text-lg font-bold">Subject rankings</h2>
            <Table
              className="mt-3"
              head={["Subject", "Your average", "National average", "Rank", "Percentile"]}
              rows={data.subjects.map((s) => [s.subject, PCT(s.schoolAvgPct), PCT(s.nationalAvgPct), ORD(s.rank, s.totalSchools), PCT(s.percentile)])}
              empty="No subject has enough benchmarked schools yet."
            />
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <h2 className="font-display text-lg font-bold">Topic rankings</h2>
            <Table
              className="mt-3"
              head={["Topic", "Subject", "Your average", "National average", "Rank"]}
              rows={data.topics.map((t) => [t.topic, t.subject, PCT(t.schoolAvgPct), PCT(t.nationalAvgPct), ORD(t.rank, t.totalSchools)])}
              empty="Topic mastery data will appear as learners complete more work."
            />
          </section>

          <section className="rounded-2xl border border-border bg-card p-5 shadow-soft">
            <h2 className="font-display text-lg font-bold">Class rankings</h2>
            <Table
              className="mt-3"
              head={["Class", "Learners", "Your average", "National average", "Rank", "Percentile"]}
              rows={data.classes.map((c) => [c.className, String(c.learners), PCT(c.schoolAvgPct), PCT(c.nationalAvgPct), ORD(c.rank, c.totalSchools), PCT(c.percentile)])}
              empty="No class has enough learners to be benchmarked."
            />
          </section>
        </div>
      )}
    </AdminShell>
  );
}

function Stat({ icon: Icon, label, value, note }: { icon: typeof Globe2; label: string; value: string; note?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary-soft text-primary"><Icon className="h-5 w-5" /></div>
      <div className="mt-3 font-display text-2xl font-bold">{value}</div>
      <div className="text-xs font-semibold">{label}</div>
      {note && <div className="mt-1 text-xs text-muted-foreground">{note}</div>}
    </div>
  );
}

function Bar({ label, value, highlight }: { label: string; value: number; highlight?: boolean }) {
  return (
    <div className="mt-3">
      <div className="flex items-center justify-between text-sm">
        <span className={cn(highlight && "font-semibold")}>{label}</span>
        <span className="tabular-nums text-muted-foreground">{value}%</span>
      </div>
      <div className="mt-1 h-2.5 overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full rounded-full", highlight ? "bg-primary" : "bg-muted-foreground/40")}
          style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
      </div>
    </div>
  );
}

function Table({ head, rows, empty, className }: { head: string[]; rows: string[][]; empty: string; className?: string }) {
  if (!rows.length) return <p className={cn("text-sm text-muted-foreground", className)}>{empty}</p>;
  return (
    <div className={cn("overflow-x-auto", className)}>
      <table className="w-full min-w-[520px] text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
            {head.map((h) => <th key={h} className="pb-2 pr-4 font-semibold">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-border">
              {r.map((c, j) => <td key={j} className="py-2 pr-4 tabular-nums">{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
